import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const ConsultationForm = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    company: '',
    role: '',
    projectType: '',
    budget: '',
    timeline: '',
    message: '',
    newsletter: false
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const projectTypeOptions = [
    { value: 'mvp', label: 'MVP Development' },
    { value: 'saas', label: 'SaaS Platform' },
    { value: 'enterprise', label: 'Enterprise Solution' },
    { value: 'mobile', label: 'Mobile Application' },
    { value: 'web', label: 'Web Application' },
    { value: 'consulting', label: 'Technical Consulting' },
    { value: 'other', label: 'Other' }
  ];

  const budgetOptions = [
    { value: '25k-50k', label: '$25,000 - $50,000' },
    { value: '50k-100k', label: '$50,000 - $100,000' },
    { value: '100k-250k', label: '$100,000 - $250,000' },
    { value: '250k+', label: '$250,000+' },
    { value: 'flexible', label: 'Flexible / To be discussed' }
  ];

  const timelineOptions = [
    { value: 'urgent', label: 'Urgent (1-2 months)' },
    { value: 'short', label: 'Short-term (3-4 months)' },
    { value: 'medium', label: 'Medium-term (5-8 months)' },
    { value: 'long', label: 'Long-term (9+ months)' },
    { value: 'flexible', label: 'Flexible timeline' }
  ];

  const roleOptions = [
    { value: 'ceo', label: 'CEO / Founder' },
    { value: 'cto', label: 'CTO / Technical Lead' },
    { value: 'product', label: 'Product Manager' },
    { value: 'procurement', label: 'Procurement / Vendor Management' },
    { value: 'other', label: 'Other' }
  ];

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.fullName?.trim()) {
      newErrors.fullName = 'Full name is required';
    }

    if (!formData?.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData?.phone?.trim()) {
      newErrors.phone = 'Phone number is required';
    }

    if (!formData?.company?.trim()) {
      newErrors.company = 'Company name is required';
    }

    if (!formData?.role) {
      newErrors.role = 'Please select your role';
    }

    if (!formData?.projectType) {
      newErrors.projectType = 'Please select a project type';
    }

    if (!formData?.message?.trim()) {
      newErrors.message = 'Please provide project details';
    } else if (formData?.message?.trim()?.length < 50) {
      newErrors.message = 'Please provide at least 50 characters describing your project';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    
    if (validateForm()) {
      setIsSubmitting(true);
      
      setTimeout(() => {
        setIsSubmitting(false);
        setSubmitSuccess(true);
        setFormData({
          fullName: '',
          email: '',
          phone: '',
          company: '',
          role: '',
          projectType: '',
          budget: '',
          timeline: '',
          message: '',
          newsletter: false
        });
        
        setTimeout(() => {
          setSubmitSuccess(false);
        }, 5000);
      }, 2000);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <section className="py-12 md:py-16 lg:py-20 bg-background">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8 md:mb-12">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold font-headline text-foreground mb-3 md:mb-4">
            Schedule Your Free Consultation
          </h2>
          <p className="text-sm md:text-base lg:text-lg text-muted-foreground font-body max-w-2xl mx-auto">
            Tell us about your project and we'll get back to you within 24 hours with a customized proposal and next steps.
          </p>
        </div>

        {submitSuccess && (
          <div className="mb-6 md:mb-8 p-4 md:p-6 bg-success/10 border border-success rounded-lg flex items-start space-x-3">
            <Icon name="CheckCircle2" size={24} color="var(--color-success)" className="flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-base md:text-lg font-semibold text-success mb-1">Thank you for reaching out!</h3>
              <p className="text-sm md:text-base text-success/80">
                We've received your consultation request and will respond within 24 hours. Check your email for confirmation.
              </p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="bg-card border border-border rounded-xl p-6 md:p-8 lg:p-10 shadow-lg">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-6 md:mb-8">
            <Input
              label="Full Name"
              type="text"
              placeholder="John Doe"
              value={formData?.fullName}
              onChange={(e) => handleChange('fullName', e?.target?.value)}
              error={errors?.fullName}
              required
            />

            <Input
              label="Email Address"
              type="email"
              placeholder="john@company.com"
              value={formData?.email}
              onChange={(e) => handleChange('email', e?.target?.value)}
              error={errors?.email}
              required
            />

            <Input
              label="Phone Number"
              type="tel"
              placeholder="Mobile Number"
              value={formData?.phone}
              onChange={(e) => handleChange('phone', e?.target?.value)}
              error={errors?.phone}
              required
            />

            <Input
              label="Company Name"
              type="text"
              placeholder="Your Company Inc."
              value={formData?.company}
              onChange={(e) => handleChange('company', e?.target?.value)}
              error={errors?.company}
              required
            />

            <Select
              label="Your Role"
              placeholder="Select your role"
              options={roleOptions}
              value={formData?.role}
              onChange={(value) => handleChange('role', value)}
              error={errors?.role}
              required
            />

            <Select
              label="Project Type"
              placeholder="Select project type"
              options={projectTypeOptions}
              value={formData?.projectType}
              onChange={(value) => handleChange('projectType', value)}
              error={errors?.projectType}
              required
            />

            {/* <Select
              label="Budget Range"
              placeholder="Select budget range"
              options={budgetOptions}
              value={formData?.budget}
              onChange={(value) => handleChange('budget', value)}
              description="Optional - helps us prepare a tailored proposal"
            /> */}

            <Select
              label="Timeline"
              placeholder="Select timeline"
              options={timelineOptions}
              value={formData?.timeline}
              onChange={(value) => handleChange('timeline', value)}
              description="Optional - when do you need to launch?"
            />
          </div>

          <div className="mb-6 md:mb-8">
            <label className="block text-sm font-medium text-foreground mb-2">
              Project Details <span className="text-destructive">*</span>
            </label>
            <textarea
              className="w-full min-h-[120px] md:min-h-[150px] px-4 py-3 bg-background border border-input rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent resize-y font-body text-sm md:text-base"
              placeholder="Tell us about your project goals, challenges, and what you're looking to achieve. The more details you provide, the better we can prepare for our consultation."
              value={formData?.message}
              onChange={(e) => handleChange('message', e?.target?.value)}
            />
            {errors?.message && (
              <p className="mt-2 text-sm text-destructive flex items-center">
                <Icon name="AlertCircle" size={16} className="mr-1" />
                {errors?.message}
              </p>
            )}
            <p className="mt-2 text-xs md:text-sm text-muted-foreground">
              {formData?.message?.length} / 50 characters minimum
            </p>
          </div>

          <div className="mb-6 md:mb-8">
            {/* <Checkbox
              label="Subscribe to our newsletter for SaaS development insights and industry trends"
              checked={formData?.newsletter}
              onChange={(e) => handleChange('newsletter', e?.target?.checked)}
            /> */}
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-6 border-t border-border">
            <p className="text-xs md:text-sm text-muted-foreground font-body text-center sm:text-left">
              By submitting this form, you agree to our privacy policy and terms of service.
            </p>
            <Button
              type="submit"
              variant="default"
              size="lg"
              loading={isSubmitting}
              iconName="Send"
              iconPosition="right"
              className="w-full sm:w-auto"
            >
              {isSubmitting ? 'Submitting...' : 'Schedule Consultation'}
            </Button>
          </div>
        </form>

        <div className="mt-6 md:mt-8 text-center">
          <p className="text-xs md:text-sm text-muted-foreground font-body">
            <Icon name="Clock" size={16} className="inline mr-1" />
            Average response time: 4-6 hours during business hours
          </p>
        </div>
      </div>
    </section>
  );
};

export default ConsultationForm;